import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchCarrierLoads, selectCarrierLoads, selectActiveTab, setActiveTab, setLoadTransitioning, updateCarrierLoad } from '../store/slices/loadCarrierSlice';
import { Truck, Calendar, MapPin, Package, DollarSign, Filter, Search, ChevronDown, ChevronUp, CheckCircle, Info, ChevronLeft, ChevronRight, Menu, Navigation, Flag, AlertCircle, AlertTriangle, Building2, Weight, Box, Ruler, Copy, X, FileText, Eye, RotateCcw } from 'lucide-react';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { fetchCarrierLoadDocuments } from '../store/slices/loadCarrierSlice';
import ReactDOM from 'react-dom';
import LoadStatusTimeline from '../components/LoadStatusTimeline';
import { fetchNotifications } from '../store/slices/notificationSlice';
import StatusUpdateModal from '../components/StatusUpdateModal';
import DocumentCard from '../components/DocumentCard';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { LoadActionButtons } from '../components/LoadActionButtons';
import api, { exceptionApi } from '../utils/api';
import { computeFirstPickup, computeLastDelivery, getLoadDisplayId } from '../utils/loadUtils';
import { formatDate } from '../utils/dateTimeUtils';
import { formatDateTime } from '../utils/formatters';

// Helper to get exception border/styling classes based on severity
const getExceptionStyles = (severity) => {
  const styles = {
    low: {
      border: 'border-yellow-400',
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      icon: 'text-yellow-500',
      badge: 'bg-yellow-100 text-yellow-800',
      button: 'bg-yellow-500 hover:bg-yellow-600'
    },
    medium: {
      border: 'border-orange-400',
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      icon: 'text-orange-500',
      badge: 'bg-orange-100 text-orange-800',
      button: 'bg-orange-500 hover:bg-orange-600'
    },
    high: {
      border: 'border-red-400',
      bg: 'bg-red-50',
      text: 'text-red-700',
      icon: 'text-red-500',
      badge: 'bg-red-100 text-red-800',
      button: 'bg-red-500 hover:bg-red-600'
    },
    critical: {
      border: 'border-red-500',
      bg: 'bg-red-50',
      text: 'text-red-800',
      icon: 'text-red-600',
      badge: 'bg-red-200 text-red-900',
      button: 'bg-red-600 hover:bg-red-700'
    }
  };
  return styles[severity] || styles.medium;
};

const STATUS_CONFIG = {
  open: {
    color: 'neutral',
    label: 'Open for Bids',
    icon: Package,
    action: null,
    next: null
  },
  assigned: {
    color: 'primary',
    label: 'Awaiting Driver',
    icon: Truck,
    action: 'Assign Driver',
    next: 'assigned_driver'
  },
  assigned_driver: {
    color: 'primary',
    label: 'Driver Assigned',
    icon: MapPin,
    action: 'Start Trip',
    next: 'trip_started'
  },
  trip_started: {
    color: 'active',
    label: 'Trip Started',
    icon: Navigation,
    action: 'Arrive at First Stop',
    next: 'at_stop_0'
  },
  cancelled: {
    color: 'error',
    label: 'Cancelled',
    icon: X,
    action: null,
    next: null
  },
  completed: {
    color: 'success',
    label: 'Completed',
    icon: CheckCircle,
    action: null,
    next: null
  },
  
  getConfig: (status, load) => {
    if (!status) {
      return {
        color: 'gray',
        label: 'Status Unavailable',
        icon: AlertCircle,
        action: 'Update Status',
        next: null
      };
    }

    // Ensure status is a clean lowercase string without surrounding whitespace
    const cleanStatus = String(status).trim().toLowerCase();

    // First check exact matches including cancelled and completed
    if (STATUS_CONFIG[cleanStatus]) {
      return STATUS_CONFIG[cleanStatus];
    }

    // Handle stop-based statuses
    if (cleanStatus.startsWith('at_stop_')) {
      const stopNumber = parseInt(cleanStatus.split('_')[2]);
      const isLastStop = load?.stops && stopNumber === load.stops.length - 1;
      
      return {
        color: 'active',
        label: `At Stop ${stopNumber + 1}${isLastStop ? ' (Final Stop)' : ''}`,
        icon: Navigation,
        action: isLastStop ? 'Complete Delivery' : 'Start Next Transit',
        next: isLastStop ? 'completed' : `in_transit_${stopNumber + 1}`
      };
    }
    
    if (cleanStatus.startsWith('in_transit_')) {
      return {
        color: 'active',
        label: 'In Transit',
        icon: Flag,
        action: 'Mark At Next Stop',
        next: 'at_stop'
      };
    }

    // Default fallback
    return {
      color: 'neutral',
      label: 'Status Unavailable',
      icon: AlertCircle,
      action: 'Update Status',
      next: null
    };
  }
};

// Exception Panel Component with Resolve Capability
const ExceptionPanel = ({ load, onExceptionResolved, isMobile }) => {
  const [resolvingId, setResolvingId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const dispatch = useDispatch();

  const activeExceptions = load.exceptions?.filter(e => !e.is_resolved) || [];
  const resolvedExceptions = load.exceptions?.filter(e => e.is_resolved) || [];

  if (!load.exceptions || load.exceptions.length === 0) return null;

  const handleResolve = async (exceptionId) => {
    setResolvingId(exceptionId);
    try {
      const response = await exceptionApi.resolveException(load.id, exceptionId, resolutionNotes);
      
      // Update the load in the store with the new exception data
      if (response.data && response.data.load) {
        dispatch(updateCarrierLoad(response.data.load));
      }
      
      toast.success('Exception resolved successfully');
      setShowResolveForm(null);
      setResolutionNotes('');
      
      if (onExceptionResolved) {
        onExceptionResolved(exceptionId);
      }
    } catch (error) {
      console.error('Failed to resolve exception:', error);
      toast.error(error.response?.data?.error || 'Failed to resolve exception');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      {/* Active Exceptions */}
      {activeExceptions.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Active Exceptions ({activeExceptions.length})
          </h4>
          {activeExceptions.map((exc) => {
            const styles = getExceptionStyles(exc.severity);
            const isResolving = resolvingId === exc.id;
            const showingForm = showResolveForm === exc.id;

            return (
              <div 
                key={exc.id} 
                className={`p-4 rounded-xl border-l-4 ${styles.border} bg-white shadow-sm`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${styles.text}`}>
                        {exc.exception_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                        {exc.severity}
                      </span>
                      {exc.is_automated && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          Automated
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{exc.notes}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Reported {formatDateTime(exc.created_at)}
                      {exc.created_by_name && ` by ${exc.created_by_name}`}
                    </p>
                  </div>
                  
                  {/* Resolve Button */}
                  {!showingForm && (
                    <button
                      onClick={() => setShowResolveForm(exc.id)}
                      disabled={isResolving}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-white text-sm font-medium transition-colors ${styles.button} disabled:opacity-50`}
                    >
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Resolve
                    </button>
                  )}
                </div>

                {/* Resolution Form */}
                <AnimatePresence>
                  {showingForm && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-gray-100"
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Resolution Notes (optional)
                      </label>
                      <textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        placeholder="Describe how this exception was resolved..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-brand focus:border-transparent"
                        rows={3}
                        disabled={isResolving}
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <button
                          onClick={() => {
                            setShowResolveForm(null);
                            setResolutionNotes('');
                          }}
                          disabled={isResolving}
                          className="px-3 py-1.5 rounded-lg text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleResolve(exc.id)}
                          disabled={isResolving}
                          className="px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {isResolving ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </motion.div>
                              Resolving...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Confirm Resolution
                            </>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Resolved Exceptions (Collapsible) */}
      {resolvedExceptions.length > 0 && (
        <div className={activeExceptions.length > 0 ? 'mt-4' : ''}>
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            {showResolved ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {resolvedExceptions.length} Resolved Exception{resolvedExceptions.length > 1 ? 's' : ''}
          </button>
          <AnimatePresence>
            {showResolved && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 space-y-2"
              >
                {resolvedExceptions.map((exc) => (
                  <div key={exc.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-600">
                        {exc.exception_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{exc.notes}</p>
                    {exc.resolution_notes && (
                      <p className="text-xs text-green-600 mt-1">
                        <span className="font-medium">Resolution:</span> {exc.resolution_notes}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Resolved {exc.resolved_at && formatDateTime(exc.resolved_at)}
                      {exc.resolved_by_name && ` by ${exc.resolved_by_name}`}
                    </p>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

// Status options for filtering within the active tab
const CARRIER_STATUS_FILTER_OPTIONS = {
  active: [
    { value: 'all', label: 'All Active' },
    { value: 'assigned', label: 'Awaiting Driver' },
    { value: 'assigned_driver', label: 'Driver Assigned' },
    { value: 'trip_started', label: 'Trip Started' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'at_stop', label: 'At Stop' },
  ],
  completed: [{ value: 'all', label: 'All Completed' }],
  cancelled: [{ value: 'all', label: 'All Cancelled' }],
};

const CarrierLoads = () => {
  const dispatch = useDispatch();
  const loads = useSelector(selectCarrierLoads);
  const activeTab = useSelector(selectActiveTab);
  const { pagination, status, error } = useSelector((state) => state.loadCarrier);
  const user = useSelector((state) => state.auth.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [statusModalLoad, setStatusModalLoad] = useState(null);
  const [filteredLoads, setFilteredLoads] = useState([]);
  const loadTransitioning = useSelector(state => state.loadCarrier.loadTransitioning);
  const [statusFilter, setStatusFilter] = useState('all');

  // Add ref for StatusUpdateModal
  const statusModalRef = useRef(null);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Check for automated exceptions (late arrivals) on page load
  useEffect(() => {
    const checkForExceptions = async () => {
      try {
        const response = await exceptionApi.checkExceptions();
        if (response.data?.exceptions_created > 0) {
          toast.info(
            `🔔 ${response.data.exceptions_created} late arrival${response.data.exceptions_created > 1 ? 's' : ''} detected`,
            { autoClose: 5000 }
          );
          // Refresh loads to get updated exception data
          dispatch(fetchCarrierLoads({ 
            page: currentPage, 
            perPage: 10,
            status: activeTab,
            search: debouncedQuery,
            sortBy: sortBy,
            sortOrder: sortOrder
          }));
        }
      } catch (error) {
        // Silently fail - this is a background check
        console.debug('Exception check failed:', error);
      }
    };
    
    checkForExceptions();
  }, []); // Only run on initial mount

  // Here we add a debounced search state so the backend is hit only after the user pauses typing.
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  useEffect(() => {
    dispatch(fetchCarrierLoads({ 
      page: currentPage, 
      perPage: 10,
      status: activeTab,
      search: debouncedQuery,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  }, [dispatch, currentPage, activeTab, debouncedQuery, sortBy, sortOrder]);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab));
    setCurrentPage(1);
    setStatusFilter('all'); // Reset status filter when changing tabs
    dispatch(fetchCarrierLoads({ 
      page: 1, 
      status: tab,
      search: debouncedQuery,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    dispatch(fetchCarrierLoads({ 
      page: newPage, 
      perPage: pagination.perPage,
      status: activeTab === 'active' ? 'active' : 'completed', 
      search: debouncedQuery,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  };

  const handleSort = (newSortBy) => {
    const newSortOrder = sortBy === newSortBy && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
    dispatch(fetchCarrierLoads({ 
      page: 1, 
      status: activeTab === 'active' ? 'active' : 'completed', 
      search: debouncedQuery,
      sortBy: newSortBy,
      sortOrder: newSortOrder
    }));
  };

  const openStatusModal = (load) => {
    setStatusModalLoad(load);
  };

  const closeStatusModal = () => {
    setStatusModalLoad(null);
  };

  const filterLoads = useCallback(() => {
    let filtered = loads.filter(load => {
      // Normalize status to lowercase for consistent comparison
      const normalizedStatus = (load.status || '').toLowerCase();
      const matchesTab = 
        (activeTab === 'active' && normalizedStatus !== 'completed' && normalizedStatus !== 'cancelled') ||
        (activeTab === 'completed' && normalizedStatus === 'completed') ||
        (activeTab === 'cancelled' && normalizedStatus === 'cancelled');
      
      // Apply status filter within the tab
      let matchesStatusFilter = true;
      if (statusFilter !== 'all' && activeTab === 'active') {
        if (statusFilter === 'in_transit') {
          matchesStatusFilter = normalizedStatus.startsWith('in_transit');
        } else if (statusFilter === 'at_stop') {
          matchesStatusFilter = normalizedStatus.startsWith('at_stop');
        } else {
          matchesStatusFilter = normalizedStatus === statusFilter;
        }
      }
        
      // Helper function to safely check if a string includes the search term
      const safeStringIncludes = (value, searchTerm) => {
        return value && typeof value === 'string' ? value.toLowerCase().includes(searchTerm) : false;
      };

      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        load.id.toString().includes(searchQuery) ||
        safeStringIncludes(load.origin_city, searchLower) ||
        safeStringIncludes(load.destination_city, searchLower) ||
        safeStringIncludes(load.load_type, searchLower);
        
      return matchesTab && matchesStatusFilter && matchesSearch;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created_at':
          return sortOrder === 'desc' 
            ? new Date(b.created_at) - new Date(a.created_at)
            : new Date(a.created_at) - new Date(b.created_at);
        // Add other sort cases as needed
        default:
          return 0;
      }
    });

    setFilteredLoads(filtered);
  }, [loads, activeTab, searchQuery, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    filterLoads();
  }, [filterLoads]);

  const handleStatusUpdate = (updatedLoad) => {
    // Update Redux store with the updated load data
    dispatch(updateCarrierLoad({ id: updatedLoad.id, data: updatedLoad }));
    
    // Update local state immediately for better UX
    const updatedLoads = loads.map(load => 
      load.id === updatedLoad.id ? updatedLoad : load
    );
    setFilteredLoads(updatedLoads.filter(load => {
      const status = (load.status || '').toLowerCase();
      return activeTab === 'active' ? status !== 'completed' && status !== 'cancelled' : status === 'completed';
    }));
    
    closeStatusModal();
  };

  const tabs = [
    { id: 'active', label: 'Active Loads', icon: Navigation },
    { id: 'completed', label: 'Completed', icon: CheckCircle },
    { id: 'cancelled', label: 'Cancelled', icon: X }
  ];

  const renderFilterSection = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6 mb-6">
        {/* Search Section */}
        <div className="w-full lg:flex-1 lg:max-w-md">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search loads by ID, origin, destination..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
            />
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex bg-gray-50 rounded-xl p-1 w-full lg:w-auto overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`relative flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-brand shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="w-4 h-4" />
                <span className={isMobile ? 'hidden' : 'block'}>{tab.label}</span>
                {activeTab === tab.id && (
                  <motion.div
                    className="absolute inset-0 bg-white rounded-lg shadow-sm -z-10"
                    layoutId="activeTab"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Status Filter and Sort Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Status Filter - Only show when there are multiple options */}
        {CARRIER_STATUS_FILTER_OPTIONS[activeTab]?.length > 1 && (
          <div className="relative min-w-[160px]">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-xl py-2.5 pl-9 pr-8 bg-white appearance-none cursor-pointer hover:border-gray-300 focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
            >
              {CARRIER_STATUS_FILTER_OPTIONS[activeTab]?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
        )}
        
        {/* Sort Controls */}
        <div className="flex flex-wrap gap-2">
          <SortButton 
            onClick={() => handleSort('pickup_date')} 
            active={sortBy === 'pickup_date'}
            order={sortOrder}
          >
            <Calendar className="w-4 h-4" />
            <span className={isMobile ? 'sr-only' : ''}>Pickup Date</span>
          </SortButton>
          <SortButton 
            onClick={() => handleSort('delivery_date')} 
            active={sortBy === 'delivery_date'}
            order={sortOrder}
          >
            <Flag className="w-4 h-4" />
            <span className={isMobile ? 'sr-only' : ''}>Delivery Date</span>
          </SortButton>
          <SortButton 
            onClick={() => handleSort('price')} 
            active={sortBy === 'price'}
            order={sortOrder}
          >
            <DollarSign className="w-4 h-4" />
            <span className={isMobile ? 'sr-only' : ''}>Price</span>
          </SortButton>
        </div>
      </div>
    </div>
  );

  const renderLoadsList = () => (
    <div className="space-y-4">
      <AnimatePresence mode="popLayout" initial={false}>
        {filteredLoads.map((load) => (
          <motion.div
            key={load.id}
            layout="position"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
              opacity: { duration: 0.2 }
            }}
          >
            <CarrierLoadItem 
              load={load} 
              user={user}
              onUpdateStatus={() => openStatusModal(load)}
              isTransitioning={loadTransitioning === load.id}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );

  const renderPagination = () => (
    <motion.div 
      layout
      className="mt-6 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
      transition={{ duration: 0.2 }}
    >
      <div className="flex-1 flex justify-between sm:hidden w-full mb-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Previous
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === pagination.pages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Next
        </button>
      </div>
      <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="mb-4 sm:mb-0">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * pagination.perPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * pagination.perPage, pagination.total)}</span> of{' '}
            <span className="font-medium">{pagination.total}</span> results
          </p>
        </div>
        <div className="hidden sm:flex">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <span className="sr-only">Previous</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <span className="sr-only">Next</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </motion.div>
  );

  if (status === 'loading') {
    return <LoadingSpinner />;
  }

  if (status === 'failed') {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />
      <main className={`flex-1 overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          {(isMobile || isTablet) && (
            <motion.button
              onClick={toggleSidebar}
              className="mb-4 p-2 bg-white rounded-md shadow-md"
              whileTap={{ scale: 0.95 }}
            >
              <Menu className="h-6 w-6" />
            </motion.button>
          )}
          
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
              My Assigned Loads
            </h1>
            <p className="text-gray-600 text-lg">Manage and track your active shipments</p>
          </div>
          
          {renderFilterSection()}
          
          {filteredLoads.length > 0 ? renderLoadsList() : <EmptyState />}
          
          {filteredLoads.length > 0 && renderPagination()}
        </div>
      </main>

      <AnimatePresence>
        {statusModalLoad && (
          <StatusUpdateModal 
            ref={statusModalRef}
            isOpen={!!statusModalLoad}
            onClose={closeStatusModal}
            load={statusModalLoad}
            currentStatus={statusModalLoad?.status ? String(statusModalLoad.status).toLowerCase() : 'unknown'}
            onStatusUpdate={handleStatusUpdate}
          />
        )}
      </AnimatePresence>

      <ToastContainer
        position={isMobile ? "bottom-center" : "bottom-right"}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={3}
      />
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-full">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
  </div>
);

const ErrorMessage = ({ error }) => (
  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-8" role="alert">
    <p className="font-bold">Error</p>
    <p>{error}</p>
  </div>
);

const FilterButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
      active ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
    }`}
  >
    {children}
  </button>
);

const EmptyState = () => (
  <div className="text-center py-12">
    <Package className="mx-auto h-12 w-12 text-gray-400" />
    <h3 className="mt-2 text-sm font-medium text-gray-900">No loads</h3>
    <p className="mt-1 text-sm text-gray-500">Get started by accepting new loads.</p>
  </div>
);

const SortButton = ({ onClick, active, order, children }) => (
  <motion.button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
      active 
        ? 'bg-brand/10 text-brand border border-brand/20' 
        : 'bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200'
    }`}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    {children}
    {active && (
      <motion.div
        initial={{ rotate: 0 }}
        animate={{ rotate: order === 'desc' ? 180 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <ChevronUp className="w-4 h-4" />
      </motion.div>
    )}
  </motion.button>
);

// Update CarrierLoadItem component
const CarrierLoadItem = React.forwardRef(({ load, user, onUpdateStatus, isTransitioning }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const { isMobile } = useScreenSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get active exceptions for border styling
  const activeExceptions = load.exceptions?.filter(e => !e.is_resolved) || [];
  const hasActiveException = activeExceptions.length > 0;
  const highestSeverity = activeExceptions.reduce((highest, exc) => {
    const order = { low: 0, medium: 1, high: 2, critical: 3 };
    return order[exc.severity] > order[highest] ? exc.severity : highest;
  }, 'low');
  const exceptionStyles = hasActiveException ? getExceptionStyles(highestSeverity) : null;
  
  // Add document loading handler
  const handleExpandDocuments = async () => {
    if (!documentsLoaded) {
      await dispatch(fetchCarrierLoadDocuments(load.id));
      setDocumentsLoaded(true);
    }
  };
  
  const canRevertTripStart = useCallback(() => {
    const normalizedStatus = (load.status || '').toLowerCase();
    if (normalizedStatus !== 'trip_started' || !load.trip_started_at) {
      return false;
    }
    
    const tripStartTime = parseUTCDate(load.trip_started_at);
    const now = new Date();
    const gracePeriodMs = (load.trip_start_grace_period || 300) * 1000; // Convert seconds to milliseconds
    
    return (now - tripStartTime) <= gracePeriodMs;
  }, [load.status, load.trip_started_at, load.trip_start_grace_period]);
  
  const handleRevertSuccess = (updatedLoad) => {
    // Call the parent's onUpdateStatus with the updated load
    onUpdateStatus(updatedLoad);
  };

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: isTransitioning ? 0.7 : 1,
        y: isTransitioning ? -10 : 0,
        scale: isTransitioning ? 0.98 : 1,
      }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ 
        type: "spring",
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.3 }
      }}
      className={`bg-white rounded-2xl shadow-sm border-2 ${hasActiveException ? exceptionStyles.border : 'border-gray-100'} relative overflow-hidden ${isMobile ? 'p-3' : 'p-6'} hover:shadow-md transition-all duration-300 ${
        isTransitioning ? 'pointer-events-none' : ''
      } ${hasActiveException ? 'ring-1 ring-opacity-20 ' + (
        highestSeverity === 'critical' ? 'ring-red-500' :
        highestSeverity === 'high' ? 'ring-red-400' :
        highestSeverity === 'medium' ? 'ring-orange-400' : 'ring-yellow-400'
      ) : ''}`}
    >
      {/* Subtle overlay when transitioning */}
      {isTransitioning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.05 }}
          className="absolute inset-0 bg-brand pointer-events-none"
        />
      )}

      {/* Subtle Brand Background */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-brand/5 to-indigo-500/5 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2" />

      {/* Enhanced Status Badge - Only show absolute positioned on desktop */}
      {!isMobile && <StatusBadge status={load.status} load={load} />}
      
      <div className="relative z-10">
        {/* Top section with load info and action buttons */}
        <div className="flex flex-col space-y-3">
          {/* Mobile: Status badge inline at top */}
          {isMobile && (
            <div className="flex justify-end -mb-1">
              <StatusBadge status={load.status} load={load} isMobile={true} />
            </div>
          )}
          
          {/* Load header */}
          <div className="flex items-start gap-3">
            <div className={`${isMobile ? 'w-9 h-9' : 'w-12 h-12'} rounded-xl bg-gradient-to-br from-brand/10 to-indigo-500/10 flex items-center justify-center border border-brand/10 flex-shrink-0`}>
              <Package className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'} text-brand`} />
            </div>
            <div className="flex-1 min-w-0">
              {/* Mobile: Stack load ID and title; Desktop: Inline */}
              <div className={`${isMobile ? 'flex flex-col gap-1' : 'flex items-center gap-2'}`}>
                <h3 className={`${isMobile ? 'text-sm' : 'text-xl'} font-bold text-gray-900`}>
                  Load
                </h3>
                <span className={`${isMobile ? 'text-[10px] px-1.5 py-0.5 w-fit' : 'text-sm px-2 py-0.5'} bg-gray-100 rounded-md text-gray-700 font-mono`}>
                  #{getLoadDisplayId(load)}
                </span>
              </div>
              <p className={`${isMobile ? 'text-xs mt-1.5 leading-tight' : 'text-base mt-1'} text-gray-600 truncate`}>
                {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
              </p>
            </div>
          </div>

          {/* Action buttons row */}
          <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}>
            {/* Primary action buttons */}
            <div className={`flex ${isMobile ? 'flex-wrap gap-2' : 'items-center space-x-3'}`}>
              <ActionButton 
                status={load.status} 
                onClick={onUpdateStatus}
                load={load}
                isMobile={isMobile}
              />
              
              {canRevertTripStart() && (
                <RevertTripStartButton
                  load={load}
                  onSuccess={handleRevertSuccess}
                />
              )}
              
              <LoadActionButtons 
                load={load} 
                isMobile={isMobile}
                canShare={(load.status || '').toLowerCase() !== 'open'}
              />
            </div>
            
            {/* Expand/collapse button - inline on mobile, absolute on desktop */}
            {isMobile ? (
              <div className="flex justify-end">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600 text-xs font-medium"
                >
                  {isExpanded ? 'Show Less' : 'Show More'}
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </motion.div>
                </button>
              </div>
            ) : (
              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="
                  absolute top-6 right-6 
                  p-2 rounded-xl 
                  bg-white/90 backdrop-blur-sm 
                  hover:bg-gray-50 
                  transition-all duration-200
                  shadow-sm
                  border border-gray-200
                  hover:border-gray-300
                  z-10
                "
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                </motion.div>
              </motion.button>
            )}
          </div>
        </div>

        {/* Exception Banner - Shows in collapsed view */}
        {!isExpanded && hasActiveException && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className={`${exceptionStyles.bg} border-t ${exceptionStyles.border} ${isMobile ? 'px-3 py-2 -mx-3' : 'px-4 py-3 -mx-6'} mt-3`}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${exceptionStyles.icon} flex-shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ${exceptionStyles.text}`}>
                    {activeExceptions.length === 1 ? 'Exception' : `${activeExceptions.length} Exceptions`}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${exceptionStyles.badge}`}>
                    {activeExceptions[0].exception_type.replace(/_/g, ' ')}
                  </span>
                </div>
                {!isMobile && (
                  <p className={`text-xs ${exceptionStyles.text} opacity-80 mt-0.5 truncate`}>
                    {activeExceptions[0].notes}
                  </p>
                )}
              </div>
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 whitespace-nowrap`}>
                {isMobile ? 'Expand' : 'Expand to resolve'}
              </span>
            </div>
          </motion.div>
        )}

        {/* Expandable content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="space-y-6 mt-4 pt-4 border-t border-gray-100"
            >
              {/* Exception Panel - Shows active and resolved exceptions with resolve capability */}
              {load.exceptions?.length > 0 && (
                <ExceptionPanel 
                  load={load} 
                  isMobile={isMobile}
                />
              )}

              {/* Enhanced Timeline */}
              <div>
                <LoadStatusTimeline loadId={load.id} isCarrier={true} />
              </div>

              {/* Load Details Grid */}
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 md:grid-cols-4 gap-4'}`}>
                <DetailCard
                  icon={Package}
                  label="Load Type"
                  value={load.load_type}
                />
                <DetailCard
                  icon={Weight}
                  label="Weight"
                  value={`${load.weight} lbs`}
                />
                <DetailCard
                  icon={Box}
                  label="Pieces"
                  value={load.pieces}
                />
                <DetailCard
                  icon={Ruler}
                  label="Dimensions"
                  value={formatDimensions(load.dimensions)}
                />
              </div>

              {/* Schedule and Location Information */}
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-6'}`}>
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-500" />
                    Initial Pickup Location
                  </h4>
                  {(() => {
                    const fp = computeFirstPickup(load);
                    return (
                      <TimeCard
                        icon={Calendar}
                        label="Pickup Appointment"
                        date={fp.date}
                        time={fp.time}
                      />
                    );
                  })()}
                  <AddressCard
                    label="Pickup Address"
                    address={(() => { const fp = computeFirstPickup(load); return formatAddress(fp.address, fp.city, fp.state, fp.zip); })()}
                    isPickup={true}
                  />
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Flag className="w-5 h-5 text-green-500" />
                    Final Delivery Location
                  </h4>
                  {(() => {
                    const ld = computeLastDelivery(load);
                    return (
                      <TimeCard
                        icon={Calendar}
                        label="Delivery Appointment"
                        date={ld.date}
                        time={ld.time}
                      />
                    );
                  })()}
                  <AddressCard
                    label="Delivery Address"
                    address={(() => { const ld = computeLastDelivery(load); return formatAddress(ld.address, ld.city, ld.state, ld.zip); })()}
                    isDelivery={true}
                  />
                </div>
              </div>

              {/* Bid Information Card */}
              {load.bids?.find(bid => bid.carrier_id === load.assigned_to_carrier) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-100"
                >
                  <h4 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Assigned Bid
                  </h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-green-600">Amount</p>
                        <p className="text-xl font-bold text-green-700">
                          ${load.bids.find(bid => bid.carrier_id === load.assigned_to_carrier).amount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-green-600">User</p>
                        <p className="text-lg text-green-700">
                          {load.bids.find(bid => bid.carrier_id === load.assigned_to_carrier).bidder_name}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Load Documents Section */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Load Documents
                </h4>
                
                <div className="grid grid-cols-1 gap-3">
                  {!documentsLoaded ? (
                    <button 
                      onClick={handleExpandDocuments}
                      className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" /> View Documents
                    </button>
                  ) : (
                    <>
                      {load.documents?.map((doc) => (
                        <DocumentCard 
                          key={doc.id} 
                          document={doc}
                          isMobile={isMobile}
                        />
                      ))}
                      {(!load.documents || load.documents.length === 0) && (
                        <p className="text-sm text-gray-500 italic">No documents available</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});

// Add display name for debugging
CarrierLoadItem.displayName = 'CarrierLoadItem';

// Update TimeCard component to match CustomerShipments
const TimeCard = ({ icon: Icon, label, date, time, address }) => {
  const { isMobile } = useScreenSize();
  
  return (
    <motion.div 
      className={`p-${isMobile ? '4' : '6'} bg-white/60 backdrop-blur-sm rounded-xl border border-gray-100 hover:border-indigo-100 transition-all duration-300`}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-indigo-500" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="font-semibold text-gray-900">
            {formatDate(date) || 'Invalid Date'}
            {time && <span className="ml-2 text-gray-500">{formatTime(time)}</span>}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Update DetailCard component to match CustomerShipments
const DetailCard = ({ icon: Icon, label, value }) => {
  const { isMobile } = useScreenSize();
  
  return (
      <motion.div
    className={`group p-${isMobile ? '3' : '4'} bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 hover:border-brand/20 transition-all duration-300`}
    whileHover={{ scale: 1.02 }}
  >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center group-hover:bg-brand/15 transition-colors duration-300">
          <Icon className="w-4 h-4 text-brand" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Professional color system based on semantic meaning
const getStatusColor = (status) => {
  const colorMap = {
    'assigned': 'primary',
    'assigned_driver': 'primary',
    'at_pickup': 'active',
    'in_transit': 'active',
    'at_delivery': 'active',
    'completed': 'success',
    'cancelled': 'error',
    'default': 'neutral'
  };
  const safeStatus = status ? String(status).toLowerCase() : 'default';
  return colorMap[safeStatus] || colorMap.default;
};

// Update the getStatusIcon function
const getStatusIcon = (status) => {
  const iconMap = {
    'assigned': CheckCircle,
    'assigned_driver': Truck,
    'at_pickup': MapPin,
    'in_transit': Navigation,
    'at_delivery': Flag,
    'completed': CheckCircle,
    'default': Info
  };
  return iconMap[status] || iconMap.default;
};

// Update the StatusStamp component
const StatusStamp = ({ status, load }) => {
  const currentStatus = status ? String(status).toLowerCase() : '';
  const config = STATUS_CONFIG.getConfig(currentStatus, load);
  
  const Icon = config.icon;
  
  const colorClasses = {
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500',
    gray: 'bg-gray-500'
  };
  
  return (
    <div className={`
      absolute top-0 right-0 
      transform translate-x-2 -translate-y-1/2 
      px-3 py-1.5 
      rounded-full 
      text-sm font-semibold
      ${colorClasses[config.color]}
      text-white
      shadow-sm
      flex items-center space-x-1.5
    `}>
      <Icon className="w-4 h-4" />
      <span>{config.label}</span>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-sm font-medium text-gray-600">{label}</p>
    <p className="text-base text-gray-800">{value}</p>
  </div>
);

const DateTimeInfo = ({ label, date, time }) => (
  <div>
    <p className="text-sm font-medium text-gray-600">{label}</p>
    <p className="text-base text-gray-800">
      {formatDate(date) || 'Invalid Date'} {time && formatTime(time)}
    </p>
  </div>
);

const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
};

const formatDimensions = (dimensions) => {
  if (!dimensions) return 'N/A';
  return `${dimensions.length}x${dimensions.width}x${dimensions.height}`;
};

const formatAddress = (address, city, state, zip) => {
  return `${address}, ${city}, ${state} ${zip}`;
};

// Update the ActionButton component
const ActionButton = ({ status, onClick, load, isMobile }) => {
  const currentStatus = status ? String(status).toLowerCase() : '';
  const config = STATUS_CONFIG.getConfig(currentStatus, load);
  
  // Don't show action button for completed or cancelled loads
  if (currentStatus === 'completed' || currentStatus === 'cancelled') return null;
  
  const Icon = config.icon;
  
  const colorVariants = {
    primary: 'bg-brand/10 text-brand hover:bg-brand/15 border-brand/20',
    active: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200',
    success: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200',
    error: 'bg-red-50 text-red-700 hover:bg-red-100 border-red-200',
    warning: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200',
    neutral: 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
  };
  
  return (
    <motion.button
      onClick={onClick}
      className={`
        inline-flex items-center gap-2
        px-4 py-2.5
        ${colorVariants[config.color]}
        rounded-xl
        font-medium
        text-sm
        transition-all duration-200
        border
        hover:shadow-sm
        ${isMobile ? 'flex-1 justify-center' : ''}
      `}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Icon className="w-4 h-4" />
      <span>{isMobile ? config.action?.split(' ')[0] : config.action}</span>
    </motion.button>
  );
};

// Add StatusBadge component with mobile support
const StatusBadge = ({ status, load, isMobile = false }) => {
  // Add better null safety for status
  const safeStatus = status || 'unknown';
  const config = STATUS_CONFIG.getConfig(safeStatus, load);
  const Icon = config.icon;
  
  const colorVariants = {
    primary: 'bg-gradient-to-r from-brand to-blue-600',
    active: 'bg-gradient-to-r from-indigo-500 to-purple-600',
    success: 'bg-gradient-to-r from-emerald-500 to-green-600',
    error: 'bg-gradient-to-r from-red-500 to-red-600',
    warning: 'bg-gradient-to-r from-amber-500 to-orange-600',
    neutral: 'bg-gradient-to-r from-gray-500 to-gray-600'
  };

  // Mobile: inline badge; Desktop: absolute positioned
  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`
          inline-flex items-center gap-1.5
          px-2.5 py-1
          rounded-full 
          ${colorVariants[config.color]}
          shadow-sm
          border border-white/20
        `}
      >
        <Icon className="w-3 h-3 text-white" />
        <span className="text-xs font-medium text-white">
          {config.label}
        </span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`
        absolute top-0 right-12 
        transform translate-x-2 -translate-y-1/2 
        px-4 py-2
        rounded-full 
        ${colorVariants[config.color]}
        shadow-sm
        border border-white/20
      `}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-white" />
        <span className="text-sm font-medium text-white">
          {config.label}
        </span>
      </div>
    </motion.div>
  );
};

// Update AddressCard component to include pickup/delivery styling
const AddressCard = ({ label, address, isPickup, isDelivery }) => (
  <motion.div
    className={`p-6 bg-white/80 backdrop-blur-sm rounded-xl border transition-all duration-300 ${
      isPickup ? 'hover:border-brand/30 border-brand/10' :
      isDelivery ? 'hover:border-emerald-300 border-emerald-100' :
      'hover:border-gray-200 border-gray-100'
    }`}
    whileHover={{ scale: 1.02 }}
  >
    <div className="flex items-start gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isPickup ? 'bg-brand/10' :
        isDelivery ? 'bg-emerald-50' :
        'bg-gray-50'
      }`}>
        <MapPin className={`w-5 h-5 ${
          isPickup ? 'text-brand' :
          isDelivery ? 'text-emerald-600' :
          'text-gray-500'
        }`} />
      </div>
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <div className="space-y-1">
          <p className="font-medium text-gray-900 leading-relaxed">
            {address}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`text-sm flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors ${
                isPickup ? 'text-brand hover:bg-brand/10' :
                isDelivery ? 'text-emerald-600 hover:bg-emerald-50' :
                'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`)}
            >
              <Navigation className="w-4 h-4" />
              Get Directions
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`text-sm flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors ${
                isPickup ? 'text-brand hover:bg-brand/10' :
                isDelivery ? 'text-emerald-600 hover:bg-emerald-50' :
                'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => navigator.clipboard.writeText(address)}
            >
              <Copy className="w-4 h-4" />
              Copy Address
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Helper function to parse database datetime strings to proper UTC Date objects
function parseUTCDate(dateStr) {
  if (!dateStr) return null;
  
  let isoStr = dateStr;
  
  // If the format is "YYYY-MM-DD HH:MM:SS.ssssss(...)" (with a space)
  // replace the space with 'T' to conform to ISO 8601.
  if (isoStr.length >= 10 && isoStr[10] === ' ') {
    isoStr = isoStr.slice(0, 10) + 'T' + isoStr.slice(11);
  }
  
  // JavaScript Date only supports milliseconds (3 decimals).
  // Truncate any extra fractional digits (microseconds) beyond the first three.
  isoStr = isoStr.replace(/(\.\d{3})\d+/, '$1');
  
  // If no timezone information is present (i.e. no trailing 'Z', '+' or '-' with offset),
  // we assume the timestamp is in UTC and append a 'Z'.
  if (!/(Z|[+-]\d{2}:\d{2})$/.test(isoStr)) {
    isoStr += 'Z';
  }
  
  return new Date(isoStr);
}

const RevertTripStartButton = ({ load, onSuccess }) => {
  const [isReverting, setIsReverting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => {
    const tripStartTime = parseUTCDate(load.trip_started_at);
    if (!tripStartTime) return 0;
    
    const gracePeriodMs = (load.trip_start_grace_period || 300) * 1000;
    const expiryTime = tripStartTime.getTime() + gracePeriodMs;
    const now = Date.now();
    const remaining = Math.max(0, expiryTime - now);
    
    // Don't show button if more than grace period has elapsed
    if (now - tripStartTime.getTime() > gracePeriodMs) {
      return 0;
    }
    
    return remaining;
  });

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timerId = setInterval(() => {
      const tripStartTime = parseUTCDate(load.trip_started_at);
      if (!tripStartTime) {
        setTimeLeft(0);
        return;
      }

      const gracePeriodMs = (load.trip_start_grace_period || 300) * 1000;
      const now = Date.now();
      
      // Check if we're past the grace period
      if (now - tripStartTime.getTime() > gracePeriodMs) {
        setTimeLeft(0);
        return;
      }
      
      const expiryTime = tripStartTime.getTime() + gracePeriodMs;
      const remaining = Math.max(0, expiryTime - now);
      setTimeLeft(remaining);
    }, 1000);

    return () => clearInterval(timerId);
  }, [load.trip_started_at, load.trip_start_grace_period]);

  // Don't render if no time left
  if (timeLeft <= 0) return null;

  const handleRevert = async () => {
    try {
      setIsReverting(true);
      const response = await api.post(`/api/carrier/loads/${load.id}/revert-trip-start`);

      if (response.data) {
        toast.success('Trip start successfully reverted');
        onSuccess(response.data.load);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to revert trip start';
      toast.error(errorMessage);
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <motion.button
      onClick={handleRevert}
      disabled={isReverting}
      className={`
        inline-flex items-center gap-2
        px-4 py-2.5
        bg-amber-50 text-amber-700 
        hover:bg-amber-100
        rounded-xl
        font-medium
        text-sm
        transition-all duration-200
        border border-amber-200
        hover:border-amber-300
        hover:shadow-sm
        disabled:opacity-50
        disabled:cursor-not-allowed
        ${isReverting ? 'animate-pulse' : ''}
      `}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <RotateCcw className={`w-4 h-4 ${isReverting ? 'animate-spin' : ''}`} />
      <span>
        Revert Trip Start
        <span className="ml-1 text-amber-500">
          ({Math.ceil(timeLeft / 1000)}s)
        </span>
      </span>
    </motion.button>
  );
};

export default CarrierLoads;
